import React, { useState, useEffect } from 'react';
import { User as UserIcon } from 'lucide-react';

interface SafeAvatarProps {
  src?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-12 h-12 text-base',
  lg: 'w-16 h-16 text-xl',
  xl: 'w-24 h-24 text-3xl',
};

export const SafeAvatar: React.FC<SafeAvatarProps> = ({ 
  src, 
  name = 'Utilisateur', 
  size = 'sm', 
  className = "" 
}) => {
  const [imgError, setImgError] = useState(false);
  const [imgUrl, setImgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!src) {
      setImgUrl(null);
      return;
    }

    // Gestion du préfixe API
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    let fullUrl = src;

    if (src.startsWith('/') && !src.startsWith('http')) {
      fullUrl = `${apiUrl}${src}`;
    } else if (!src.startsWith('http')) {
      fullUrl = `${apiUrl}/${src}`;
    }

    // Cache busting : on ajoute un paramètre de temps si c'est une image de profil
    // pour s'assurer que les updates sont instantanés
    const finalUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    
    setImgUrl(finalUrl);
    setImgError(false);
  }, [src]);

  // Génération des initiales
  const getInitials = (n: string) => {
    return n
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Dégradé déterministe basé sur le nom
  const getGradient = (n: string) => {
    const gradients = [
      'from-blue-600 to-indigo-700',
      'from-emerald-500 to-teal-700',
      'from-purple-600 to-indigo-800',
      'from-rose-500 to-red-700',
      'from-amber-500 to-orange-700',
    ];
    let hash = 0;
    for (let i = 0; i < n.length; i++) {
      hash = n.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
  };

  if (imgUrl && !imgError) {
    return (
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden shadow-inner bg-slate-100 flex items-center justify-center ${className}`}>
        <img 
          src={imgUrl} 
          alt={name} 
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${getGradient(name)} flex items-center justify-center text-white font-bold shadow-lg ${className}`}>
      {name !== 'Utilisateur' ? (
        getInitials(name)
      ) : (
        <UserIcon className="w-1/2 h-1/2" />
      )}
    </div>
  );
};
